import React from 'react';
import { X, Mail, Phone, Clock, MessageSquare, Plane, Calendar, AlertCircle, FileText } from 'lucide-react';

const ExecutiveInfoModal = ({ executive, onClose }) => {
  if (!executive) return null;

  const parseJSON = (jsonString) => {
    try {
      return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    } catch {
      return {};
    }
  };

  // Use travel_preferences if it exists (new format), otherwise fall back to travel_preferences_json
  const travelPrefs = executive.travel_preferences || parseJSON(executive.travel_preferences_json) || {};
  const meetingPrefs = parseJSON(executive.meeting_preferences_json) || {};
  const importantDates = parseJSON(executive.important_dates_json) || {};

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}
      onClick={onClose}
    >
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 25px 70px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#F9FAFB'
        }}>
          <div>
            <h2 style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: '0 0 4px 0' }}>
              {executive.full_name}
            </h2>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              {executive.title}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#6B7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px'
        }}>
          {/* Contact Information */}
          <div style={{ marginBottom: '28px' }}>
            <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Contact Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {executive.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Mail size={16} color="#6B7280" />
                  <a href={`mailto:${executive.email}`} style={{ color: '#3B82F6', fontSize: '14px', textDecoration: 'none' }}>
                    {executive.email}
                  </a>
                </div>
              )}
              {executive.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Phone size={16} color="#6B7280" />
                  <a href={`tel:${executive.phone}`} style={{ color: '#3B82F6', fontSize: '14px', textDecoration: 'none' }}>
                    {executive.phone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Work Hours & Availability */}
          {executive.work_hours && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={18} color="#3B82F6" />
                Work Hours & Availability
              </h3>
              <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>
                {executive.work_hours}
              </p>
            </div>
          )}

          {/* Preferred Contact Method */}
          {executive.preferred_contact_method && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MessageSquare size={18} color="#3B82F6" />
                Preferred Contact Method
              </h3>
              <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>
                {executive.preferred_contact_method}
              </p>
            </div>
          )}

          {/* Meeting Preferences */}
          {Object.keys(meetingPrefs).length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} color="#3B82F6" />
                Meeting Preferences
              </h3>
              <div style={{ background: '#F9FAFB', padding: '14px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                {meetingPrefs.preferred_days && (
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                    <strong>Preferred Days:</strong> {meetingPrefs.preferred_days}
                  </p>
                )}
                {meetingPrefs.preferred_times && (
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                    <strong>Preferred Times:</strong> {meetingPrefs.preferred_times}
                  </p>
                )}
                {meetingPrefs.buffer_time && (
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                    <strong>Buffer Time:</strong> {meetingPrefs.buffer_time}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Travel Preferences */}
          {Object.keys(travelPrefs).length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plane size={18} color="#3B82F6" />
                Travel Preferences
              </h3>
              <div style={{ background: '#F9FAFB', padding: '14px', borderRadius: '8px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Airlines */}
                {travelPrefs.airlines && travelPrefs.airlines.length > 0 && (
                  <div>
                    <p style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px 0' }}>
                      Preferred Airlines
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {travelPrefs.airlines.map((airline, idx) => (
                        <span key={idx} style={{
                          background: '#DBEAFE',
                          color: '#1E40AF',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          {airline}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seat Preferences */}
                {travelPrefs.seat && (
                  <div>
                    <p style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px 0' }}>
                      Seat Preferences
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {travelPrefs.seat.preference && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#6B7280', fontSize: '13px' }}>Position:</span>
                          <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500', textTransform: 'capitalize' }}>
                            {travelPrefs.seat.preference}
                          </span>
                        </div>
                      )}
                      {travelPrefs.seat.cabin && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#6B7280', fontSize: '13px' }}>Cabin:</span>
                          <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500', textTransform: 'capitalize' }}>
                            {travelPrefs.seat.cabin}
                          </span>
                        </div>
                      )}
                      {travelPrefs.seat.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#6B7280', fontSize: '13px' }}>Location:</span>
                          <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500', textTransform: 'capitalize' }}>
                            {travelPrefs.seat.location}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hotel Preferences */}
                {travelPrefs.hotels?.preferred_brands && travelPrefs.hotels.preferred_brands.length > 0 && (
                  <div>
                    <p style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px 0' }}>
                      Preferred Hotels
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {travelPrefs.hotels.preferred_brands.map((hotel, idx) => (
                        <span key={idx} style={{
                          background: '#FEF3C7',
                          color: '#92400E',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          {hotel}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rental Car */}
                {travelPrefs.rental_car?.class && (
                  <div>
                    <p style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px 0' }}>
                      Rental Car Preference
                    </p>
                    <span style={{
                      background: '#E0E7FF',
                      color: '#3730A3',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'inline-block',
                      textTransform: 'capitalize'
                    }}>
                      {travelPrefs.rental_car.class}
                    </span>
                  </div>
                )}

                {/* TSA Programs */}
                {(travelPrefs.tsa_programs?.tsa_precheck || travelPrefs.tsa_programs?.global_entry) && (
                  <div>
                    <p style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px 0' }}>
                      TSA Programs
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {travelPrefs.tsa_programs.tsa_precheck && (
                        <span style={{
                          background: '#D1FAE5',
                          color: '#065F46',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          TSA PreCheck
                        </span>
                      )}
                      {travelPrefs.tsa_programs.global_entry && (
                        <span style={{
                          background: '#D1FAE5',
                          color: '#065F46',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Global Entry
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Important Dates */}
          {Object.keys(importantDates).length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} color="#3B82F6" />
                Important Dates
              </h3>
              <div style={{ background: '#FEF9E7', padding: '14px', borderRadius: '8px', border: '1px solid #FDE68A' }}>
                {importantDates.birthday && (
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                    <strong>Birthday:</strong> {importantDates.birthday}
                  </p>
                )}
                {importantDates.anniversary && (
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                    <strong>Anniversary:</strong> {importantDates.anniversary}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Emergency Escalation */}
          {executive.emergency_contact_policy && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={18} color="#DC2626" />
                Emergency Escalation
              </h3>
              <div style={{ background: '#FEF2F2', padding: '14px', borderRadius: '8px', border: '1px solid #FCA5A5' }}>
                <p style={{ color: '#7F1D1D', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                  {executive.emergency_contact_policy}
                </p>
              </div>
            </div>
          )}

          {/* Notes for Assistant */}
          {executive.notes_for_assistant && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} color="#3B82F6" />
                How to Work With Me
              </h3>
              <div style={{ background: '#EFF6FF', padding: '14px', borderRadius: '8px', border: '1px solid #DBEAFE' }}>
                <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {executive.notes_for_assistant}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid #E5E7EB',
          background: '#F9FAFB',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveInfoModal;
